import {
  ArrowDropDown,
  CheckBox,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { Enum, useAllEnums } from '~/data/enums';
import { UnitPositionCustomData } from '~/data/unitPositions';
import { scheduledDateFormats } from '~/features/ScheduledEmploymentChanges/helpers';
import { useSxStyles } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/UnitPositionComponent/UnitPositionCustomDataPanel/useStyles';
import {
  IndexedUpCustomDataAttribute,
  UpCustomData,
  UpCustomDataAttribute,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/data';
import { filterActiveEnums } from '~/utils/helpers/enum';

type PropsType = {
  customData: UnitPositionCustomData;
  unitPositionCustomData: UpCustomData[];
  unitPositionCustomDataAttributes?: IndexedUpCustomDataAttribute;
  isLoading: boolean;
  failedToLoad: boolean;
};

export const UnitPositionCustomDataPanel = (props: PropsType) => {
  const {
    customData,
    unitPositionCustomData,
    unitPositionCustomDataAttributes,
    isLoading,
    failedToLoad,
  } = props;

  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { allEnums } = useAllEnums();

  const enumNameField = (enumValue: Enum[], id: string) => {
    if (id === undefined || id === '') return '';
    const specificEnum = enumValue.find((en) => en.code === id);
    return specificEnum === undefined ? id : specificEnum.name;
  };

  const resolveCustomFieldValue = (
    code: string,
    upCustomData: UnitPositionCustomData,
  ) => {
    if (
      !upCustomData.hasOwnProperty(code) ||
      !unitPositionCustomDataAttributes?.hasOwnProperty(code)
    ) {
      return null;
    }

    const customDataAttributes: UpCustomDataAttribute =
      unitPositionCustomDataAttributes[code];

    const strName = customDataAttributes.fCustomDataAttributeName;
    const strType = customDataAttributes.fCustomDataAttributeDataType;

    if (!customDataAttributes || !upCustomData.hasOwnProperty(strName)) {
      return '';
    }
    switch (strType) {
      case 'BOOLEAN':
        return upCustomData[strName] === 'true' ? (
          <CheckBox />
        ) : (
          <CheckBoxOutlineBlank />
        );
      case 'TEXT':
      case 'STRING':
        return upCustomData[strName];
      case 'DATE':
        return moment(
          upCustomData[strName],
          scheduledDateFormats.dashFormat,
        ).format(scheduledDateFormats.dotFormat);
      case 'NUMBER':
        return upCustomData[strName]
          ? upCustomData[strName].toString().replace('.', ',')
          : '';
      case 'ENUM':
        return enumNameField(
          filterActiveEnums(
            allEnums[
              customDataAttributes.fCustomDataAttributeDataSubtype as string
            ],
          ),
          upCustomData[strName] as string,
        );
      case 'MULTISELECT':
        const strMultiValues = (upCustomData[strName] as string[])
          .map((up) =>
            enumNameField(
              filterActiveEnums(
                allEnums[
                  customDataAttributes.fCustomDataAttributeDataSubtype as string
                ],
              ),
              up,
            ),
          )
          .join(', ');

        return strMultiValues.slice(0, -2);
      default:
        return '';
    }
  };

  if (isLoading) {
    return <CircularProgress />;
  }

  if (failedToLoad) {
    return <></>;
  }

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ArrowDropDown />}>
        {t('laCustomData')}
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <Divider />
        <Table size={'small'}>
          <TableBody>
            {unitPositionCustomData.map((upc) => {
              const { name, code } = upc;

              return (
                <TableRow key={code}>
                  <TableCell sx={sxStyles.customDataLabel}>{name}</TableCell>
                  <TableCell>
                    {resolveCustomFieldValue(code, customData)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};
